/**
 * Stacked router - orchestrates tabs and stack overlays based on URL.
 *
 * NORTHSTARS:
 * - React Router is boss - we listen to useLocation(), never call pushState
 * - Less code, better
 * - One job - orchestrate rendering based on URL
 */

import { Suspense, useEffect, useMemo, useRef } from "react";
import {
  matchRoutes,
  useLocation,
  useNavigationType,
  type Location,
  type RouteMatch,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { APP_ROUTES, type StackedRouteHandle, type TabId } from "@/routes/routeObjects";
import { useNavigationStack, type StackEntry } from "@/contexts/NavigationStackContext";
import ScrollContainer from "@/components/layout/ScrollContainer";
import { useAppBack } from "@/hooks/useAppBack";
import { RouteGround } from "@/components/shared/RouteGround";
import { TabContainer } from "./TabContainer";
import { StackOverlay } from "./StackOverlay";
import { StackSurfaceContext } from "./StackSurfaceContext";
import { readHistoryIdx, resolveStackUnderIdx, type SeenLocation } from "./stackDismissModel";

const buildStackEntry = (location: Location): StackEntry => {
  const { pathname, search, hash, state } = location;
  return {
    id: `${pathname}${search}${hash}`,
    pathname,
    search,
    hash,
    state: state ?? null,
    underIdx: null,
  };
};

const buildTabLocation = (nextLocation: Location) => ({
  id: `${nextLocation.pathname}${nextLocation.search}${nextLocation.hash}`,
  pathname: nextLocation.pathname,
  search: nextLocation.search,
  hash: nextLocation.hash,
  state: nextLocation.state ?? null,
});

const getActiveStackedHandle = (matches: RouteMatch[]): StackedRouteHandle | null => {
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const handle = matches[i]?.route?.handle as StackedRouteHandle | undefined;
    if (handle && (handle.type === "tab" || handle.type === "stack" || handle.type === "page")) {
      return handle;
    }
  }
  return null;
};

export function StackedRouter() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const matches = matchRoutes(APP_ROUTES, location) ?? [];
  const activeHandle = getActiveStackedHandle(matches);
  const handleStackDismiss = useAppBack();

  const { activeTab, stack, lastActiveTab, setActiveTab, pushStack, popStack, clearStack, setTabLocation } =
    useNavigationStack();

  // Remember which tab was underneath the stack when a tab transition clears it,
  // so browser-back can restore the correct background tab.
  const prevTabForStackRef = useRef<TabId | null>(null);
  const hasRenderedTabRef = useRef(false);
  const renderedTabRef = useRef<TabId | null>(null);
  // The last location this router rendered, and where it sat in history —
  // what a newly pushed stack entry is actually opened over.
  const seenLocationRef = useRef<SeenLocation | null>(null);

  if (activeHandle?.type === "tab") {
    hasRenderedTabRef.current = true;
    renderedTabRef.current = activeHandle.tabId ?? null;
  }

  useEffect(() => {
    const previous = seenLocationRef.current;
    const currentIdx = readHistoryIdx();
    seenLocationRef.current = { id: `${location.pathname}${location.search}${location.hash}`, idx: currentIdx };

    if (!activeHandle) return;

    if (activeHandle.type === "tab" && activeHandle.tabId) {
      setTabLocation(activeHandle.tabId, buildTabLocation(location));
      if (activeHandle.tabId !== activeTab || stack.length > 0) {
        // Save the tab that was under the stack before we clear it
        if (stack.length > 0) {
          prevTabForStackRef.current = activeTab;
        }
        setActiveTab(activeHandle.tabId);
        clearStack();
      }
      return;
    }

    if (activeHandle.type === "stack") {
      const entry = buildStackEntry(location);
      const topOfStack = stack[stack.length - 1];

      if (topOfStack?.id === entry.id) {
        return;
      }

      const indexInStack = stack.findIndex(item => item.id === entry.id);

      if (indexInStack >= 0) {
        const popCount = stack.length - indexInStack - 1;
        for (let i = 0; i < popCount; i += 1) {
          popStack();
        }
      } else {
        pushStack({
          ...entry,
          underIdx: resolveStackUnderIdx({
            navigationType,
            currentIdx,
            topUnderIdx: topOfStack?.underIdx ?? null,
            entryId: entry.id,
            previous,
          }),
        });

        if (stack.length === 0) {
          const tab = prevTabForStackRef.current || renderedTabRef.current || activeTab || lastActiveTab || "home";
          prevTabForStackRef.current = null;
          setActiveTab(tab);
        }
      }
      return;
    }

    if (activeHandle.type === "page") {
      if (stack.length > 0) {
        clearStack();
      }
    }
  }, [
    activeHandle,
    activeTab,
    stack,
    lastActiveTab,
    location,
    navigationType,
    setActiveTab,
    setTabLocation,
    pushStack,
    popStack,
    clearStack,
  ]);

  // A stack route opened directly renders as an overlay before it joins the stack.
  const pendingStackEntry = activeHandle?.type === "stack" && stack.length === 0
    ? buildStackEntry(location)
    : null;
  const overlayEntries = pendingStackEntry ? [pendingStackEntry] : stack;

  const PageComponent = activeHandle?.type === "page" ? activeHandle.component : null;
  const hasKnownTabBackground = hasRenderedTabRef.current || activeHandle?.type === "tab";
  const renderTabs = !PageComponent && hasKnownTabBackground;
  const renderedActiveTab =
    activeHandle?.type === "tab" && activeHandle.tabId
      ? activeHandle.tabId
      : renderedTabRef.current || activeTab || lastActiveTab || "home";
  const baseSurface = useMemo(() => ({ isTop: overlayEntries.length === 0 }), [overlayEntries.length]);

  return (
    <div className="relative h-full min-h-0">
      <StackSurfaceContext.Provider value={baseSurface}>
        {renderTabs && (
          <div className="h-full min-h-0">
            <TabContainer activeTab={renderedActiveTab} allowScrollClaim={overlayEntries.length === 0} />
          </div>
        )}

        {PageComponent && (
          <ScrollContainer isActive className="h-full min-h-0 overflow-y-auto overscroll-contain">
            <Suspense fallback={<RouteGround />}>
              <PageComponent />
            </Suspense>
          </ScrollContainer>
        )}
      </StackSurfaceContext.Provider>

      <AnimatePresence>
        {overlayEntries.map((entry, index) => (
          <StackOverlay
            key={entry.id}
            entry={entry}
            onDismiss={handleStackDismiss}
            surfaceOrder={index + 1}
            isTop={index === overlayEntries.length - 1}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}
